import React, { useState } from 'react';
import { Form, Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import GLOBALS from '../../utils/Globals';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SchemaUpload = () => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const baseURLUpdateSchemas = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLUpdateSchemas
    : GLOBALS.DEV.baseURLUpdateSchemas;

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith('.xlsx')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
      setError('Please select a valid Excel file (.xlsx)');
      toast.error('Please select a valid Excel file (.xlsx)', {
        position: "bottom-right",
        autoClose: 3000
      });
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    
    const droppedFile = event.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!file) {
      setError('Please select a file');
      return;
    }

    // Show confirmation modal instead of proceeding directly
    setShowConfirmModal(true);
  };

  const handleConfirmedUpload = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('userToken');
      
      const response = await axios.post(baseURLUpdateSchemas, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      console.log('Upload response:', response.data);

      toast.success('Schema uploaded successfully!', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      setFile(null);
      // Reset the form
      document.querySelector('form').reset();

    } catch (err) {
      console.error('Upload error:', err.response || err);
      const errorMessage = err.response?.data?.message || 'Error uploading schema';
      setError(errorMessage);
      
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Upload Schema
              </h1>
              <p className="text-gray-600">
                Upload your schema file in Excel format (.xlsx)
              </p>
            </div>

            <Form onSubmit={handleSubmit}>
              <div
                className={`
                  border-2 border-dashed rounded-lg p-8 mb-4 text-center
                  transition-all duration-200 ease-in-out
                  ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
                  ${error ? 'border-red-300 bg-red-50' : ''}
                  ${isLoading ? 'opacity-50' : ''}
                `}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="mb-4">
                  <svg 
                    className={`mx-auto h-12 w-12 ${isDragActive ? 'text-blue-400' : 'text-gray-400'}`}
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 14v20c0 4.418 3.582 8 8 8h16c4.418 0 8-3.582 8-8V14M8 14c0-4.418 3.582-8 8-8h16c4.418 0 8 3.582 8 8M8 14h32"
                    />
                  </svg>
                </div>

                <Form.Control
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  disabled={isLoading}
                  className="hidden"
                />

                <div className="space-y-1">
                  <p className="text-lg font-medium text-gray-900">
                    {isDragActive ? "Drop your file here" : "Drag & drop your file here"}
                  </p>
                  <p className="text-sm text-gray-500">
                    or click to browse
                  </p>
                </div>

                {file && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mt-4 text-sm text-red-600">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={!file || isLoading}
                  className={`
                    inline-flex items-center px-4 py-2 rounded-md
                    ${!file || isLoading 
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'}
                    text-white font-medium transition-colors duration-200
                  `}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Uploading...
                    </>
                  ) : (
                    'Upload Schema'
                  )}
                </button>
              </div>
            </Form>

            <div className="mt-8 text-center text-sm text-gray-500">
              {/* <p>Current Environment: {process.env.REACT_APP_ENV}</p> */}
              <p className="mt-1">Only Excel files (.xlsx) are accepted</p>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to upload this schema file?
          <div className="mt-2 text-sm text-gray-600">
            File: {file?.name}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 ml-2"
            onClick={handleConfirmedUpload}
          >
            Confirm Upload
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SchemaUpload;
